import React from "react";
import { Navigate, RouteObject } from "react-router-dom";
import { PageNotFound } from "../pages/NotFound";
import { RouteErrorBoundary } from "./RouteErrorBoundary";
import { LayoutFullPage } from "../layouts/FullPage/FullPage";
import { DocsSsoPage } from "../pages/docsSso/DocsSsoPage";
import { OAuthConsentPage } from "../pages/oauthConsent/OAuthConsentPage";
import { PageForbidden } from "../pages/Forbidden";
import { Home } from "../pages/Home";
import ApplicationForm from "../pages/pro/applicationForm";
// import ThanksForApplying from "../pages/pro/ThanksForApplying";
import ProListing from "../pages/pro/Listing";
import { BasicPage } from "../pages/authV2/components/BasicPage";

import {
    LoginPage,
    ForgetPasswordPage,
    PostForgetPasswordPage,
    ResetPasswordPage,
    RegisterPage,
    AutoLoginRedirectPage,
    GoogleReturnPage
} from '../pages/authV2';
import ShareExperts from '../pages/pro/Listing/ShareExperts';
import ProjectSharePage from '../pages/pro/Listing/ProjectSharePage';
import OrderSharePage from '../pages/pro/Listing/OrderSharePage';
import OrderPublicPage from '../pages/pro/Listing/OrderPublicPage';
import OrderFeedbackPage from '../pages/orderFeedback/OrderFeedbackPage';
import ShareVideoShortlist from '../pages/admin/VideoShortlists/ShareVideoShortlist';
import PdpAuditPage from '../pages/publicPdpAudit/PdpAuditPage';
import ConsumerVoicePage from '../pages/publicConsumerVoice/ConsumerVoicePage';
import AiPresencePage from '../pages/publicAiPresence/AiPresencePage';
import ProposalPage from '../pages/publicProposal/ProposalPage';
import ProposalViewPage from '../pages/publicProposal/ProposalViewPage';
import { RmnBrandLayout } from '../layouts/RmnBrand/RmnBrandLayout';
import { RmnAuthGuard } from '../layouts/RmnBrand/RmnAuthGuard';
import RmnLoginPage from '../pages/rmn/RmnLoginPage';
import RmnAuditReportPage from '../pages/rmn/RmnAuditReportPage';
import RmnLegacyRedirect from '../pages/rmn/RmnLegacyRedirect';
import RmnPrefixRedirect from '../pages/rmn/RmnPrefixRedirect';
import RmnEntryRedirect from '../pages/rmn/RmnEntryRedirect';
import { PortalRoutes } from './portalRoutes';
import { PortalScope } from '../hooks/PortalScopeContext';

export const defaultNotFound = {
    path: '*',
    element: <Navigate to="/404" />
};

// Workspace pages sit behind the client-side membership gate (internal
// staff pass; real enforcement is the API's rmnControl).
const guard = (element: React.ReactElement) => <RmnAuthGuard>{element}</RmnAuthGuard>;

export const defaultRoutes: RouteObject[] = [
    {
        path: '/*',
        element: <Home />,
        // Was `<Navigate to="/404" />`, which reported every crash in the app as
        // a missing page and threw away the URL that caused it.
        errorElement: <RouteErrorBoundary />
    },
    {
        path: 'experts/apply',
        element: <ApplicationForm />
    },
    {
        path: 'pdp-audit',
        element: <PortalScope basePath=""><PdpAuditPage /></PortalScope>
    },
    {
        path: 'consumer-voice-audit',
        element: <PortalScope basePath=""><ConsumerVoicePage /></PortalScope>
    },
    {
        path: 'ai-presence-audit',
        element: <PortalScope basePath=""><AiPresencePage /></PortalScope>
    },
    {
        path: 'proposal',
        element: <PortalScope basePath=""><ProposalPage /></PortalScope>
    },
    {
        path: 'proposal/view/:token',
        element: <PortalScope basePath=""><ProposalViewPage /></PortalScope>
    },
    // The brand portal, canonical for brand clients since TDC-1609 and still
    // the workspace internal staff open for a brand. RmnBrandLayout resolves
    // :brandSlug and renders the chrome; PortalRoutes mounts the role's whole
    // page set underneath, re-rooted by router/relativize.ts.
    //
    // The catch-all child is what carries the page set, so it must stay LAST:
    // the static siblings above it (login, the legacy tool aliases) outrank it
    // in React Router's ranking and keep their meaning.
    {
        path: 'portal/:brandSlug',
        element: <RmnBrandLayout />,
        children: [
            // The bare portal URL (white-label login's default redirect, old
            // /rmn/:slug links) lands on the Dashboard, same as '/' does via
            // PortalEntry. This explicit index outranks the brand tree's own.
            { index: true, element: guard(<RmnLegacyRedirect to="/dashboard" />) },
            { path: 'login', element: <RmnLoginPage /> },
            // Old white-label tool paths (emails, bookmarks, /rmn-tools) →
            // the pages that replaced them (TDC-1499 phase 3).
            { path: 'pdp-audit', element: guard(<RmnLegacyRedirect to="/catalog/categories" />) },
            // Deep link to one audit report (async category/comparison
            // reports poll here while generating) — kept mounted.
            { path: 'pdp-audit/reports/:reportId', element: guard(<RmnAuditReportPage />) },
            { path: 'proposal', element: guard(<RmnLegacyRedirect to="/opportunities/strategies/new" />) },
            { path: 'proposals', element: guard(<RmnLegacyRedirect to="/opportunities/strategies" />) },
            { path: 'prep-pack', element: guard(<RmnLegacyRedirect to="/intelligence/prep-packs" />) },
            // The brand page set. networkRouteChildren is NOT spread here any
            // more — the relativized brand tree already carries it, and two
            // mounts of the same page is exactly the drift this replaces.
            { path: '*', element: guard(<PortalRoutes />) },
        ]
    },
    // The portal lived at /rmn/:brandSlug/* before — prefix shim for old
    // links (kept indefinitely).
    {
        path: 'rmn/:brandSlug/*',
        element: <RmnPrefixRedirect />
    },
    // Slug-agnostic entry: forwards to the user's own network portal.
    {
        path: "rmn-tools/:tool",
        element: <RmnEntryRedirect />
    },
    {
        path: "experts/share",
        element: <PortalScope basePath=""><ShareExperts /></PortalScope>
    },
    {
        path: "projects/share/:id",
        element: (
            <LayoutFullPage>
                <ProjectSharePage />
            </LayoutFullPage>
        )
    },
    {
        path: "projects/public/:token",
        element: (
            <LayoutFullPage>
                <ProjectSharePage />
            </LayoutFullPage>
        )
    },
    {
        path: "orders/share/:id",
        element: (
            <LayoutFullPage>
                <OrderSharePage />
            </LayoutFullPage>
        )
    },
    {
        path: "orders/public/:token",
        element: (
            <LayoutFullPage>
                <OrderPublicPage />
            </LayoutFullPage>
        )
    },
    {
        path: "orders/feedback/:token",
        element: (
            <LayoutFullPage>
                <OrderFeedbackPage />
            </LayoutFullPage>
        )
    },
    {
        path: "videos/shortlists/share/:id",
        element: (
            <LayoutFullPage>
                <ShareVideoShortlist />
            </LayoutFullPage>
        )
    },
    // {
    //   path: "experts/apply/complete",
    //   element: <ThanksForApplying />,
    // },
    {
        path: "/404",
        element: (
            <LayoutFullPage>
                <PageNotFound />
            </LayoutFullPage>
        )
    },
    {
        path: "/403",
        element: (
            <LayoutFullPage>
                <PageForbidden />
            </LayoutFullPage>
        )
    },
    {
        path: "/login",
        element: (
            <LayoutFullPage>
                <LoginPage />
            </LayoutFullPage>
        )
    },
    {
        // Sign-in handoff for the internal docs site (tdc-documentation).
        path: "/docs-sso",
        element: (
            <LayoutFullPage>
                <DocsSsoPage />
            </LayoutFullPage>
        )
    },
    {
        // Consent step of the MCP connector sign-in (claude.ai custom connector).
        path: "/oauth/authorize",
        element: (
            <LayoutFullPage>
                <OAuthConsentPage />
            </LayoutFullPage>
        )
    },
    {
        path: "/register",
        element: (
            <LayoutFullPage>
                <RegisterPage />
            </LayoutFullPage>
        )
    },
    {
        path: "/register/:brand",
        element: (
            <LayoutFullPage>
                <RegisterPage />
            </LayoutFullPage>
        )
    },
    // {
    //     path: "/register-pro",
    //     element: (
    //         <LayoutFullPage>
    //             <ProRegisterPage />
    //         </LayoutFullPage>
    //     )
    // },
    {
        path: "/forgot-password-submitted/:email?",
        element: (
            <LayoutFullPage>
                <PostForgetPasswordPage />
            </LayoutFullPage>
        )
    },
    {
        path: "/forgot-password",
        element: (
            <LayoutFullPage>
                <ForgetPasswordPage />
            </LayoutFullPage>
        )
    },
    {
        path: "/reset-password",
        element: (
            <LayoutFullPage>
                <ResetPasswordPage />
            </LayoutFullPage>
        )
    },
    {
        path: "/redirect",
        element: (
            <LayoutFullPage>
                <AutoLoginRedirectPage />
            </LayoutFullPage>
        )
    },
    // Where the API sends the browser back after "Continue with Google".
    {
        path: "/auth/google",
        element: (
            <LayoutFullPage>
                <GoogleReturnPage />
            </LayoutFullPage>
        )
    },
];
